import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildUiStringsColumns } from '../columns';
import {
  UI_STRING_SORT_KEYS,
  type SortDirection,
  type UiString,
  type UiStringSortKey,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<UiString> | undefined;
  onEdit: (row: UiString) => void;
  onSortChange: (sort: { sortBy?: UiStringSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(UI_STRING_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: UiStringSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as UiStringSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function UiStringsGrid({ data, onEdit, onSortChange }: Props) {
  const columnDefs = useMemo(() => buildUiStringsColumns({ onEdit }), [onEdit]);

  return (
    <div className="h-full w-full">
      <AgGridReact<UiString>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        rowHeight={48}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
